import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf8f5] py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 shadow-sm">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h2 className="text-2xl font-serif text-gray-900 mb-2">Check Your Email</h2>
              <p className="text-gray-600 mb-6">
                If an account with <strong>{email}</strong> exists, we've sent you a link to reset your password.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                The link will expire in 1 hour. Don't forget to check your spam folder.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Try another email
                </Button>
                <Link to="/login">
                  <Button className="w-full bg-[#2d4a47] hover:bg-[#1e3633] text-white">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#faf8f5] py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white p-8 shadow-sm">
          <Link 
            to="/login" 
            className="inline-flex items-center text-sm text-gray-500 hover:text-[#2d4a47] mb-6"
          >
            <ArrowLeft size={16} className="mr-1" /> Back to login
          </Link>

          <div className="text-center mb-8">
            <div className="mx-auto w-12 h-12 bg-[#f5f1eb] rounded-full flex items-center justify-center mb-4">
              <Mail className="text-[#2d4a47]" size={24} />
            </div>
            <h1 className="text-2xl font-serif text-gray-900 mb-2">Forgot Password?</h1>
            <p className="text-gray-600 text-sm">
              No worries! Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="mt-1"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2d4a47] hover:bg-[#1e3633] text-white py-5"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Remember your password?{' '}
            <Link to="/login" className="text-[#2d4a47] hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
